import React, { useEffect, useRef } from 'react'
import { Icon } from 'semantic-ui-react'
import { PlanetDeed } from '@prometheus/types'
import { useSelector } from '../../../../hooks/useSelector'
import { useGameBoardActions } from '../../hooks/useGameBoardActions'
import usePlayerActions from '../../hooks/usePlayerActions'
import { findOwner } from '../../../../utils/findOwner'
import translateOrbitingPlanetColorRaw from '@/utils/translateOrbitingPlanetColorRaw'
import { CompactPlanetCard } from '../PlayerChoosePlanetMovement/CompactPlanetCard'
import { isTravelChoiceLocked } from '../PlayerChoosePlanetMovement/compactPlanet'
import PromiseButtonV2 from '@/components/PromiseButtonV2'
import './BoardPlanetSheet.scss'

// Phone bottom sheet for a planet tapped on the Godot board. On desktop a
// board click travels immediately; on a phone canvas that made mis-taps
// spend the turn, so the tap opens this sheet instead and the action
// (Travel / Buy) is an explicit button. Non-actionable planets still show
// who owns them, so the board doubles as an inspector.
export const BoardPlanetSheet = () => {
  const { tappedPlanet, clearTappedPlanet } = useGameBoardActions()
  const { onPickChoosablePlanet } = usePlayerActions()
  const uid = useSelector(s => s.auth.uid) || ''
  const turn = useSelector(s => s.game.metadata?.turn) || 0
  const players = useSelector(s => s.game.metadata?.players) || {}
  const players_choosing_planet = useSelector(s => s.game.metadata?.players_choosing_planet) || []
  const occupied_planets = useSelector(s => Object.values(s.game.metadata?.players || {}).map(p => p.current_planet))
  const movement_options = useSelector(s => s.game.sheet?.planet_movement_options)
  const purchase_options = useSelector(s => s.game.sheet?.planet_purchase_options)
  const my_planets = useSelector(s => s.game.sheet?.planets)

  const planet = tappedPlanet as any
  const move = planet ? movement_options?.[planet] : undefined
  const buy = planet ? purchase_options?.[planet] : undefined
  const mine = planet ? my_planets?.[planet] : undefined

  // If the sheet opened on an actionable planet and the action completed
  // (travel consumed the options), close it. A purchase keeps it open —
  // `mine` appears and the sheet now shows the freshly owned planet, live.
  const wasActionable = useRef(false)
  useEffect(() => {
    if (planet) {
      wasActionable.current = !!(move || buy)
    }
    // Capture per tapped planet only — not when the options mutate later.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [planet])
  useEffect(() => {
    if (planet && wasActionable.current && !move && !buy && !mine) {
      clearTappedPlanet()
    }
  }, [planet, move, buy, mine, clearTappedPlanet])

  if (!planet) {
    return null
  }

  const orbit = planet.split(':')[0].split('|')[0]
  const color = translateOrbitingPlanetColorRaw(orbit)
  const name = planet.split(':')[1]
  const owner = findOwner(planet, players)
  const ownerLine = mine || owner === uid
    ? 'Yours'
    : owner
      ? `Owned by ${players[owner]?.name}`
      : undefined

  const moveDeed = move && (move as PlanetDeed).type === 'deed' ? (move as PlanetDeed) : undefined
  const buyDeed = buy && (buy as PlanetDeed).type === 'deed' ? (buy as PlanetDeed) : undefined

  return (
    <>
      <div className="pg-planet-sheet__scrim" onClick={clearTappedPlanet} />
      <div className="pg-planet-sheet" role="dialog" aria-label={`${name} actions`}>
        <div className="pg-planet-sheet__head">
          <span className="pg-planet-sheet__name" style={{ color }}>{name}</span>
          {ownerLine && <span className="pg-planet-sheet__owner">{ownerLine}</span>}
          <button type="button" className="pg-planet-sheet__close" onClick={clearTappedPlanet} aria-label="Close">
            <Icon name="close" />
          </button>
        </div>
        {mine ? (
          <CompactPlanetCard planet={planet} deed={mine.deed} owned ownedPlanet={mine} />
        ) : moveDeed ? (
          <CompactPlanetCard planet={planet} deed={moveDeed} owned={false} />
        ) : buyDeed ? (
          <CompactPlanetCard planet={planet} deed={buyDeed} owned={false} purchase />
        ) : move ? (
          // Movement to a non-deed destination (stargate, starbase, rift).
          <PromiseButtonV2 ignoreGameLogic
            size="sm"
            className="pg-planet-sheet__travel"
            disabled={isTravelChoiceLocked(turn, uid, players_choosing_planet, occupied_planets, planet)}
            style={{ backgroundColor: color, borderColor: color }}
            onClick={() => onPickChoosablePlanet(planet)}
          >
            <Icon name="rocket" style={{ position: 'relative', top: '-3px' }} />
            Travel
          </PromiseButtonV2>
        ) : (
          <span className="pg-planet-sheet__hint">
            Not reachable this move.
          </span>
        )}
      </div>
    </>
  )
}

export default BoardPlanetSheet
